import clsx from 'clsx';
import type {
  MetricHistoryEntry,
  ModelSchema,
  OutputDefinition,
  OutputThresholds,
  SignalDotState,
  SignalFieldMappings,
  SignalVariant,
} from '@arcanum-hq/shared-types';
import { Skeleton } from '@/components/ui/Skeleton';
import { useModel } from '@/hooks/use-models-v2';
import { HeroMetricCell } from './HeroMetricCell';
import {
  useSignalFieldResolver,
  type SignalFieldResolverContext,
} from '@/hooks/use-signal-field-resolver';
import { classifySignal } from '@/lib/signal-classification';
import type { ThresholdFormat } from '../ThresholdBand';

// deal-signal-overhaul Phase 4 Session 2 (2026-05-12).
//
// Zone 2 of the Signal page — 5-cell hero strip mirroring the Lovable
// reference. Replaces the legacy `findFirstNumber([...candidates])` array
// pattern in `<SignalOperatingAsset>` / `<SignalNewDevelopment>` with the
// schema-driven `signalFieldMappings` resolution shipped Phase 1.
//
// Per-cell pipeline (per Phase 4 blueprint § Session 2):
//   1. Look up named-range via `signalFieldMappings[key]`.
//   2. Resolve value via `resolver.resolve(key)`.
//   3. Look up `outputOverrides[namedRange].thresholds` from schema.
//   4. Compute `signalDot = classifySignal(value, thresholds)`.
//   5. Use schema output dataType → ThresholdFormat for `<HeroMetricCell>`.
//   6. Pass optional history (auth-side: `agent_state.metricHistory`).
//
// 4-state contract (per `.claude/agents/frontend.md` checklist):
//   - loading: `resolver.isReady === false` OR model schema not yet loaded → 5 skeleton cells
//   - error: caught at route level (TanStack Query error boundary); component never enters error UI itself
//   - empty: mapping absent OR value missing → `<HeroMetricCell>` renders `—` per its existing contract
//   - success: full per-cell render with chip + value + (optional) signalDot + (optional) thresholdBand + (optional) sparkline

interface HeroKeyConfig {
  mappingKey: keyof SignalFieldMappings;
  fallbackLabel: string;
  positiveTrend: 'up' | 'down';
}

// Hero key sets verified against the actual sidecar JSONs at
// `packages/agents/src/models/<id>/signal-field-mappings.json` on 2026-05-12.
//
// Op-asset: heroBasisToReplacementCost is "lower is better" (cheaper basis vs
// replacement cost = better value). New-dev's heroStabilizedNOIMargin and
// heroStabilizedCOC are both "higher is better" yields.
const HERO_KEYS_BY_VARIANT: Record<SignalVariant, ReadonlyArray<HeroKeyConfig>> = {
  'operating-asset': [
    { mappingKey: 'heroIRR', fallbackLabel: 'IRR', positiveTrend: 'up' },
    {
      mappingKey: 'heroEquityMultiple',
      fallbackLabel: 'Equity Multiple',
      positiveTrend: 'up',
    },
    {
      mappingKey: 'heroBasisToReplacementCost',
      fallbackLabel: 'Basis / Replacement',
      positiveTrend: 'down',
    },
    {
      mappingKey: 'heroGoingInCapRate',
      fallbackLabel: 'Going-In Cap Rate',
      positiveTrend: 'up',
    },
    {
      mappingKey: 'heroStabilizedDSCR',
      fallbackLabel: 'Stabilized DSCR',
      positiveTrend: 'up',
    },
  ],
  'new-development': [
    { mappingKey: 'heroIRR', fallbackLabel: 'IRR', positiveTrend: 'up' },
    {
      mappingKey: 'heroEquityMultiple',
      fallbackLabel: 'Equity Multiple',
      positiveTrend: 'up',
    },
    {
      mappingKey: 'heroStabilizedCOC',
      fallbackLabel: 'Stabilized CoC',
      positiveTrend: 'up',
    },
    {
      mappingKey: 'heroStabilizedDSCR',
      fallbackLabel: 'Stabilized DSCR',
      positiveTrend: 'up',
    },
    {
      mappingKey: 'heroStabilizedNOIMargin',
      fallbackLabel: 'Stabilized NOI Margin',
      positiveTrend: 'up',
    },
  ],
};

// Schema dataType → ThresholdBand / HeroMetricCell numeric format.
// Mirrors the lookup in `<SignalOperatingAsset>` so the cell renders match
// across the hero strip and the legacy panel during Session 5 wire-up.
const DATA_TYPE_TO_THRESHOLD_FORMAT: Record<string, ThresholdFormat> = {
  percentage: 'percentage',
  currency: 'currency',
  currency_per_sf: 'currency',
  currency_per_unit_year: 'currency',
  decimal: 'multiplier',
  integer: 'multiplier',
  months: 'multiplier',
  years: 'multiplier',
};

export interface HeroReturnsProps {
  /** Auth (conversation) OR public (deal share) resolver context. */
  context: SignalFieldResolverContext;
  /** Variant decides which 5-key set the strip renders. */
  variant: SignalVariant;
  /**
   * Model id (used to load `displayConfig` + `outputs[]` for thresholds and
   * dataType-derived format). Caller resolves from `conversation.modelId`
   * (auth) or `publicDeal.modelId` (public).
   */
  modelId: string;
  /**
   * Model version is threaded through for parity with the route-level call
   * sites; `useModel(id)` is version-agnostic today but the prop is kept so
   * future per-version cache keys don't churn this component's signature.
   */
  modelVersion?: string;
  /**
   * Optional history map keyed by named-range — populated by Phase-1's
   * `agent_state.metricHistory` ring buffer in auth mode. Public-mode
   * callers pass `null` (no live history on frozen shares).
   */
  metricHistory?: Record<string, MetricHistoryEntry[]> | null;
  className?: string;
}

interface ResolvedHeroCell {
  /** Stable React key + walkthrough probe — namedRange when mapped, mappingKey otherwise. */
  domKey: string;
  /** Schema name passed to `<HeroMetricCell>`'s `namedRange` prop (drives multiplier-override + walkthrough probe). */
  namedRange: string;
  label: string;
  value: number | null;
  format: ThresholdFormat;
  thresholds?: OutputThresholds;
  history?: MetricHistoryEntry[];
  signalDot: SignalDotState | null;
  positiveTrend: 'up' | 'down';
}

function buildHeroCell(
  cfg: HeroKeyConfig,
  schema: ModelSchema | undefined,
  resolved: unknown,
  metricHistory?: Record<string, MetricHistoryEntry[]> | null,
): ResolvedHeroCell {
  const mappings = schema?.displayConfig?.signalFieldMappings;
  const namedRange = mappings?.[cfg.mappingKey];

  // Mapping absent → variant-correct miss. Render `—` cell with no signalDot.
  if (!namedRange) {
    return {
      domKey: cfg.mappingKey,
      namedRange: cfg.mappingKey,
      label: cfg.fallbackLabel,
      value: null,
      format: 'multiplier',
      signalDot: null,
      positiveTrend: cfg.positiveTrend,
    };
  }

  const output = schema?.outputs.find((o: OutputDefinition) => o.name === namedRange);
  const format =
    output && DATA_TYPE_TO_THRESHOLD_FORMAT[output.dataType]
      ? DATA_TYPE_TO_THRESHOLD_FORMAT[output.dataType]
      : 'multiplier';
  const thresholds =
    schema?.displayConfig?.outputOverrides?.[namedRange]?.thresholds;
  const numericValue =
    typeof resolved === 'number' && Number.isFinite(resolved) ? resolved : null;
  const signalDot = classifySignal(numericValue, thresholds);
  const history = metricHistory?.[namedRange];

  return {
    domKey: namedRange,
    namedRange,
    label: output?.displayName ?? cfg.fallbackLabel,
    value: numericValue,
    format,
    thresholds,
    history,
    signalDot,
    positiveTrend: cfg.positiveTrend,
  };
}

/**
 * 5-cell hero strip rendered above the Signal-page body. Pure composition
 * over `<HeroMetricCell>` — no formatting logic lives here, just the
 * schema-driven resolver + classifier wiring.
 */
export function HeroReturns({
  context,
  variant,
  modelId,
  modelVersion: _modelVersion,
  metricHistory,
  className,
}: HeroReturnsProps) {
  const resolver = useSignalFieldResolver(context);
  // `deal-signal-overhaul` Phase 5 hotfix (2026-05-13): PREFER schema from
  // the resolver (it sources from `sharedModelView.data.schema` on the
  // public path, avoiding the 401-on-`/api/models/:id` cold-state lockup).
  // FALL BACK to a direct `useModel(modelId)` call for tests + any caller
  // pre-Phase-5 that hand-rolled the auth-side shape.
  const modelQuery = useModel(modelId || null);
  const schema = resolver.schema ?? modelQuery.data?.data;
  const keys = HERO_KEYS_BY_VARIANT[variant];

  const isReady = resolver.isReady && schema != null;

  const heroGradientStyle: React.CSSProperties = {
    background:
      'linear-gradient(180deg, rgba(0, 200, 83, 0.04) 0%, transparent 100%)',
  };

  if (!isReady) {
    return (
      <div
        data-testid="hero-returns"
        data-state="loading"
        data-variant={variant}
        className={clsx('flex items-stretch', className)}
        style={heroGradientStyle}
      >
        {keys.map((cfg, i) => (
          <div
            key={cfg.mappingKey}
            data-testid="hero-returns-skeleton-cell"
            className={clsx(
              'flex-1 flex flex-col justify-end gap-[5px] px-[14px] py-[10px] min-w-0',
              i < keys.length - 1 && 'border-r border-panel-border',
            )}
          >
            <Skeleton variant="line" className="w-1/2" />
            <Skeleton variant="line" className="w-3/4 h-5" />
          </div>
        ))}
      </div>
    );
  }

  const cells = keys.map((cfg) => buildHeroCell(cfg, schema, resolver.resolve(cfg.mappingKey), metricHistory));

  return (
    <div
      data-testid="hero-returns"
      data-state="ready"
      data-variant={variant}
      className={clsx('flex items-stretch', className)}
      style={heroGradientStyle}
    >
      {cells.map((cell, i) => (
        <HeroMetricCell
          key={cell.domKey}
          namedRange={cell.namedRange}
          label={cell.label}
          value={cell.value}
          format={cell.format}
          thresholds={cell.thresholds}
          history={cell.history}
          signalDot={cell.signalDot}
          positiveTrend={cell.positiveTrend}
          isLast={i === cells.length - 1}
        />
      ))}
    </div>
  );
}
